import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plugs, Plus, PencilSimple, Trash, Eye, ArrowsClockwise,
} from "@phosphor-icons/react";

const EMPTY = {
  name: "",
  unit_name: "wacalls-",
  remote_user: "wacalls_tunnel",
  remote_host: "",
  local_bind: "127.0.0.1",
  local_port: "15432",
  remote_bind: "127.0.0.1",
  remote_port: "5432",
  identity_file: "/etc/wacalls/ssh/id_ed25519_pg_tunnel",
  enabled: true,
  notes: "",
};

function statusBadge(st) {
  const s = st?.status || st?.state || "unknown";
  if (s === "online" || s === "active") {
    return <Badge variant="default">{s === "online" ? "Online" : "Active"}</Badge>;
  }
  if (s === "active-no-listen") {
    return <Badge variant="secondary">Active (no listen)</Badge>;
  }
  if (s === "inactive" || s === "failed") {
    return <Badge variant="outline">{s}</Badge>;
  }
  return <Badge variant="outline">{s}</Badge>;
}

export default function SshTunnel() {
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || can(user, "vpn-ssh-tunnel", "add");
  const canEdit = user?.role === "admin" || can(user, "vpn-ssh-tunnel", "edit");
  const canDelete = user?.role === "admin" || can(user, "vpn-ssh-tunnel", "delete");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vpn/ssh-tunnel");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      name: row.name || "",
      unit_name: row.unit_name || "wacalls-",
      remote_user: row.remote_user || "wacalls_tunnel",
      remote_host: row.remote_host || "",
      local_bind: row.local_bind || "127.0.0.1",
      local_port: row.local_port != null ? String(row.local_port) : "",
      remote_bind: row.remote_bind || "127.0.0.1",
      remote_port: row.remote_port != null ? String(row.remote_port) : "",
      identity_file: row.identity_file || EMPTY.identity_file,
      enabled: row.enabled !== false,
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const openView = async (row) => {
    try {
      const { data } = await api.get(`/vpn/ssh-tunnel/${row.id}`);
      setView(data.item || row);
      setViewOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const save = async () => {
    const name = (form.name || "").trim();
    const unit_name = (form.unit_name || "").trim();
    const remote_host = (form.remote_host || "").trim();
    const local_port = parseInt(form.local_port, 10);
    const remote_port = parseInt(form.remote_port, 10);
    if (!name) { toast.error("Name is required"); return; }
    if (!unit_name.startsWith("wacalls-")) { toast.error("Unit name must start with wacalls-"); return; }
    if (!remote_host) { toast.error("Remote host is required"); return; }
    if (!Number.isFinite(local_port) || !Number.isFinite(remote_port)) {
      toast.error("Enter valid local and remote ports"); return;
    }

    setSaving(true);
    try {
      const body = {
        name,
        unit_name,
        remote_user: (form.remote_user || "").trim() || "wacalls_tunnel",
        remote_host,
        local_bind: (form.local_bind || "").trim() || "127.0.0.1",
        local_port,
        remote_bind: (form.remote_bind || "").trim() || "127.0.0.1",
        remote_port,
        identity_file: (form.identity_file || "").trim(),
        enabled: !!form.enabled,
        notes: form.notes || "",
        apply_live: true,
      };
      if (edit) {
        await api.patch(`/vpn/ssh-tunnel/${edit.id}`, body);
        toast.success("Tunnel updated and applied");
      } else {
        await api.post("/vpn/ssh-tunnel", body);
        toast.success("Tunnel added and started");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete SSH tunnel “${row.name}” and stop unit ${row.unit_name}?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/vpn/ssh-tunnel/${row.id}`, { params: { apply_live: true } });
      toast.success("Tunnel deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="vpn-ssh-tunnel-page">
      <PageHeader
        title="SSH tunnel"
        subtitle="Autossh local-forward tunnels from this control-plane (e.g. PostgreSQL :15432). Status reflects systemd + listen port."
      >
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="rounded-lg" onClick={load} data-testid="ssh-tunnel-refresh">
            <ArrowsClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {canAdd && (
            <Button type="button" className="rounded-lg" onClick={openAdd} data-testid="ssh-tunnel-add">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add tunnel
            </Button>
          )}
        </div>
      </PageHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          testid="ssh-tunnel-empty"
          icon={Plugs}
          title="No SSH tunnels"
          description="Add a tunnel (remote host, local/remote ports, identity key) to manage it here."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Forward</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Listening</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`ssh-tunnel-row-${row.id}`}>
                  <TableCell className="font-medium">
                    {row.name}
                    {!row.enabled && (
                      <Badge variant="outline" className="ml-2">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.forward}</TableCell>
                  <TableCell className="font-mono text-xs">{row.unit_name}.service</TableCell>
                  <TableCell>{statusBadge(row.status)}</TableCell>
                  <TableCell>{row.status?.listening ? "yes" : "no"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => openView(row)} title="View">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(row)} title="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          type="button" size="sm" variant="ghost"
                          disabled={busyId === row.id}
                          onClick={() => remove(row)} title="Delete"
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit SSH tunnel" : "Add SSH tunnel"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PostgreSQL CDR tunnel" />
            </div>
            <div className="space-y-1.5">
              <Label>Systemd unit name</Label>
              <Input
                className="font-mono text-xs"
                value={form.unit_name}
                onChange={(e) => setForm({ ...form, unit_name: e.target.value })}
                placeholder="wacalls-pg-tunnel"
                disabled={!!edit}
              />
              <p className="text-xs text-muted-foreground">Must start with wacalls-. Locked after create.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Remote user</Label>
                <Input value={form.remote_user} onChange={(e) => setForm({ ...form, remote_user: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Remote host</Label>
                <Input
                  className="font-mono text-xs"
                  value={form.remote_host}
                  onChange={(e) => setForm({ ...form, remote_host: e.target.value })}
                  placeholder="116.203.168.117"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Local bind</Label>
                <Input className="font-mono text-xs" value={form.local_bind} onChange={(e) => setForm({ ...form, local_bind: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Local port</Label>
                <Input value={form.local_port} onChange={(e) => setForm({ ...form, local_port: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Remote bind</Label>
                <Input className="font-mono text-xs" value={form.remote_bind} onChange={(e) => setForm({ ...form, remote_bind: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Remote port</Label>
                <Input value={form.remote_port} onChange={(e) => setForm({ ...form, remote_port: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Identity file</Label>
              <Input
                className="font-mono text-xs"
                value={form.identity_file}
                onChange={(e) => setForm({ ...form, identity_file: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <Label>Enabled (start/stop systemd unit)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? "Saving…" : edit ? "Save" : "Add tunnel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Tunnel — {view?.name}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Status</span>{statusBadge(view.status)}</div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Listening</span><span>{view.status?.listening ? "yes" : "no"}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">PID</span><span>{view.status?.main_pid || "—"}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Started</span><span className="text-xs">{view.status?.started_at || "—"}</span></div>
              <hr className="border-border" />
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Unit</span><code className="font-mono text-xs">{view.unit_name}.service</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Forward</span><code className="font-mono text-xs text-right">{view.forward}</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Remote</span><code className="font-mono text-xs">{view.remote_user}@{view.remote_host}</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Identity</span><code className="font-mono text-xs break-all">{view.identity_file}</code></div>
              {view.notes ? <div><span className="text-muted-foreground">Notes</span><p className="mt-1">{view.notes}</p></div> : null}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
            {canEdit && view && (
              <Button type="button" onClick={() => { setViewOpen(false); openEdit(view); }}>Edit</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
